import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import {
  EmojiEventsOutlined,
  SecurityOutlined,
  ArrowBack,
} from "@mui/icons-material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode } from "../utils";
import { useNavigate } from "react-router-dom";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface SecurityHallOfFameProps {
  mode: string;
}

// Hall of Fame entries - add entries as researchers report valid vulnerabilities
interface HallOfFameEntry {
  researcher: string;
  date: string;
  recognition: string;
  severity?: "Critical" | "High" | "Medium" | "Low";
}

const hallOfFameEntries: Record<string, HallOfFameEntry[]> = {
  "2025": [],
  "2024": [],
};

const SecurityHallOfFame: React.FC<SecurityHallOfFameProps> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";
  // Use whitelabel security email, owner email, sales email, or construct from domain
  const securityEmail = whitelabelSettings?.securityEmail || whitelabelSettings?.ownerEmail || whitelabelSettings?.salesEmail || "security@" + window.location.hostname;

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case "Critical":
        return "error";
      case "High":
        return "warning";
      case "Medium":
        return "info";
      case "Low":
        return "success";
      default:
        return "default";
    }
  };

  const hasEntries = Object.values(hallOfFameEntries).some(
    (entries) => entries.length > 0
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Security Hall of Fame
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Recognizing security researchers who help keep {appName} safe
          </Typography>
        </Container>
      </Box>

      {/* Back Link */}
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Link
          component="button"
          onClick={() => navigate("/security")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.secondary",
            textDecoration: "none",
            "&:hover": { color: instanceConfig.primaryColor },
          }}
        >
          <ArrowBack fontSize="small" />
          Back to Security
        </Link>
      </Container>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          We would like to thank the following security researchers for their
          responsible disclosure of vulnerabilities. Their contributions help
          keep {appName} and our users safe.
        </Typography>

        {hasEntries ? (
          Object.entries(hallOfFameEntries)
            .reverse()
            .map(([year, entries]) =>
              entries.length > 0 ? (
                <Box key={year} sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    {year}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Researcher</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Recognition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Typography fontWeight="medium">
                                {entry.researcher}
                              </Typography>
                            </TableCell>
                            <TableCell>{entry.date}</TableCell>
                            <TableCell>
                              {entry.severity && (
                                <Chip
                                  label={entry.severity}
                                  size="small"
                                  color={getSeverityColor(entry.severity) as any}
                                />
                              )}
                            </TableCell>
                            <TableCell>{entry.recognition}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null
            )
        ) : (
          <Card sx={{ textAlign: "center", py: 6 }}>
            <CardContent>
              <SecurityOutlined
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                No submissions yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Be the first to report a vulnerability and get recognized!
              </Typography>
              <Link
                href={`mailto:${securityEmail}?subject=Security Vulnerability Report`}
                sx={{
                  display: "inline-block",
                  bgcolor: instanceConfig.primaryColor,
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  textDecoration: "none",
                  "&:hover": {
                    bgcolor: alpha(instanceConfig.primaryColor, 0.8),
                  },
                }}
              >
                Report a Vulnerability
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Recognition Guidelines */}
        <Card sx={{ mt: 6 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recognition Guidelines
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              To be listed in our Hall of Fame, researchers must:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Follow our{" "}
                <Link href="/vulnerability-disclosure-policy">
                  Vulnerability Disclosure Policy
                </Link>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Report a valid, previously unreported vulnerability
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Allow us reasonable time to fix the issue before public
                disclosure
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Not exploit the vulnerability beyond what is necessary for
                demonstration
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Report CTA */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography variant="h6" gutterBottom>
            Found a security issue?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Report it responsibly and you could be featured on this page.
          </Typography>
          <Link
            href="/security"
            sx={{
              color: instanceConfig.primaryColor,
              textDecoration: "none",
              fontWeight: "medium",
            }}
          >
            Learn how to report →
          </Link>
        </Box>
      </Container>

      <BrandedPageFooter currentPage="security" />
    </Box>
  );
};

export default SecurityHallOfFame;
